/**
 * Auth portal redirect helpers.
 *
 * Authentication for this app is owned by the external auth portal (it sets a
 * `.foo.com` session cookie that works across all foo.com domains). When the
 * user is not authenticated we send them to the portal with a `return_to`
 * param so it can bounce them back here after login.
 */

const AUTH_PORTAL_URL = process.env.AUTH_PORTAL_URL;

/**
 * Routes that must remain reachable WITHOUT a session — the new-user/credential
 * flows that are still served in-app. A user mid-invitation (or resetting a
 * forced password) must not be bounced to the portal.
 */
const PUBLIC_PATH_PREFIXES = [
  "/accept-invitation",
  "/setup",
  "/force-password-change",
  // experiment launch page renders standalone, outside the authed shell
  "/experiments",
];

/**
 * @param {string} [pathname] defaults to the current location pathname
 * @returns {boolean} true if the path is an in-app public route (no auth required)
 */
export function isPublicAuthPath(pathname = window.location.pathname) {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * Redirect the browser to the auth portal, preserving where the user was headed
 * via `?return_to=`. No-op on public routes so in-app credential flows aren't
 * interrupted, unless `force` is set — used by flows (like accepting an
 * invitation) that are themselves public routes but still need to send an
 * existing, logged-out user to the portal to authenticate.
 * @param {Object} [options]
 * @param {boolean} [options.force] - bypass the public-route no-op guard
 */
export function redirectToAuthPortal({ force = false } = {}) {
  if (!force && isPublicAuthPath()) return;

  if (!AUTH_PORTAL_URL) {
    console.error("AUTH_PORTAL_URL is not configured; cannot redirect to auth portal.");
    return;
  }

  let portalUrl;
  try {
    portalUrl = new URL(AUTH_PORTAL_URL);
  } catch {
    console.error(
      `AUTH_PORTAL_URL is not a valid absolute URL (${AUTH_PORTAL_URL}); cannot redirect to auth portal.`
    );
    return;
  }
  portalUrl.searchParams.set("return_to", window.location.href);
  window.location.replace(portalUrl.toString());
}
